import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Snackbar,
    Alert,
    Tooltip
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import { SectionHeader, StyledSelect } from './SharedComponents';
import {
    getApprovalRequests,
    updateRuleConfig,
    getRuleConfigs,
    getEwsRulesMetadata,
    EwsRulesMetadataDto
} from 'service/approvalWorkflow';
import LoaderComponent from 'components/Loader';
import {
    METRIC_DOMAIN_OPTIONS,
    COMPONENT_FILTER_OPTIONS,
    LAYER_TOOL_OPTIONS,
    ENFORCE_APPROVAL_BEFORE_ACTIVATION
} from './constants';
import { getRiskColor } from './helpers';
import { SavedRule } from './types';

interface ActivationRule {
    id: number;
    backendId?: string;
    ruleName: string;
    signal: string;
    domainComponentLayer: string;
    domain: string;
    component: string;
    layer: string;
    riskLevel: string;
    status: 'APPROVED' | 'ACTIVE' | 'PENDING_REVIEW' | 'DRAFT' | 'REJECTED' | 'ARCHIVED';
    overallStatus?: string;
    metricKPI: string;
    dataSource: string;
    detectionLogic: string;
    handlerAgent: string;
    notes: string;
    createdAt: string;
}

const STATUS_FILTER_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'PENDING_REVIEW', label: 'Pending Review' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'ARCHIVED', label: 'Archived' }
];

interface ActivationLogEvent {
    id: number;
    timestamp: string;
    action: string;
}

interface ActivationTabProps {
    savedRules?: SavedRule[];
    onActivationComplete?: (complete: boolean) => void;
}

const ActivationTab: React.FC<ActivationTabProps> = ({ savedRules, onActivationComplete }) => {
    const [rules, setRules] = useState<ActivationRule[]>([]);
    const [metadata, setMetadata] = useState<EwsRulesMetadataDto[]>([]);
    const [selectedRuleId, setSelectedRuleId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Filters
    const [metricDomainFilter, setMetricDomainFilter] = useState('all');
    const [componentFilter, setComponentFilter] = useState('all');
    const [layerToolFilter, setLayerToolFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    // Activation Logs & Toast
    const [logs, setLogs] = useState<ActivationLogEvent[]>([]);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'info' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    // Fetch live metadata, saved rule configs, and approval requests
    useEffect(() => {
        let isMounted = true;

        const fetchActivationData = async () => {
            setIsLoading(true);
            try {
                const [metaResp, rulesResp, approvalResp] = await Promise.allSettled([
                    getEwsRulesMetadata(),
                    getRuleConfigs(),
                    getApprovalRequests({ entityType: 'EWS', limit: 50 })
                ]);

                // 1. Process metadata signals for dynamic dropdown filtering
                let metaList: EwsRulesMetadataDto[] = [];
                if (metaResp.status === 'fulfilled' && metaResp.value) {
                    const rawMeta = metaResp.value;
                    metaList = Array.isArray(rawMeta?.data?.items)
                        ? rawMeta.data.items
                        : Array.isArray(rawMeta?.data)
                            ? rawMeta.data
                            : Array.isArray(rawMeta)
                                ? rawMeta
                                : [];
                    if (isMounted) {
                        setMetadata(metaList);
                    }
                }

                // 2. Process approval requests to extract approval statuses
                let approvalRequests: any[] = [];
                if (approvalResp.status === 'fulfilled' && approvalResp.value) {
                    const rawAppr = approvalResp.value;
                    approvalRequests = Array.isArray(rawAppr?.data?.items)
                        ? rawAppr.data.items
                        : Array.isArray(rawAppr?.data)
                            ? rawAppr.data
                            : Array.isArray(rawAppr?.items)
                                ? rawAppr.items
                                : [];
                }

                // If entityType: 'EWS' didn't return approval items, try fallback query
                if (approvalRequests.length === 0) {
                    try {
                        const fallbackResp = await getApprovalRequests({ limit: 50 });
                        const rawFallback = fallbackResp;
                        approvalRequests = Array.isArray(rawFallback?.data?.items)
                            ? rawFallback.data.items
                            : Array.isArray(rawFallback?.data)
                                ? rawFallback.data
                                : Array.isArray(rawFallback?.items)
                                    ? rawFallback.items
                                    : [];
                    } catch (fbErr) {
                        console.log('Approval requests fallback note:', fbErr);
                    }
                }

                // 3. Process saved rule configs from backend
                let fetchedConfigs: any[] = [];
                if (rulesResp.status === 'fulfilled' && rulesResp.value) {
                    const rawConfigs = rulesResp.value;
                    fetchedConfigs = Array.isArray(rawConfigs?.data?.items)
                        ? rawConfigs.data.items
                        : Array.isArray(rawConfigs?.data)
                            ? rawConfigs.data
                            : Array.isArray(rawConfigs)
                                ? rawConfigs
                                : [];
                }

                // 4. Merge rule configs, session savedRules, and approval requests
                const mergedRules: ActivationRule[] = [];
                const seenRuleKeys = new Set<string>();

                // Helper to match an approval request for a given rule
                const findMatchingApproval = (ruleBackendId?: string, ruleName?: string) => {
                    return approvalRequests.find((ar: any) => {
                        if (ruleBackendId && (ar.entityId === ruleBackendId || ar._id === ruleBackendId)) {
                            return true;
                        }
                        if (ruleName && ar.entitySnapshot?.ruleName && ar.entitySnapshot.ruleName.toLowerCase() === ruleName.toLowerCase()) {
                            return true;
                        }
                        if (ruleName && ar.entityName && ar.entityName.toLowerCase() === ruleName.toLowerCase()) {
                            return true;
                        }
                        return false;
                    });
                };

                // Helper to derive activation rule status from rule flags & approval request
                const deriveStatus = (
                    isActiveFlag?: boolean,
                    isApprovedFlag?: boolean,
                    currentStatus?: string,
                    matchedReq?: any
                ): 'APPROVED' | 'ACTIVE' | 'PENDING_REVIEW' | 'DRAFT' | 'REJECTED' | 'ARCHIVED' => {
                    if (isActiveFlag || currentStatus === 'ACTIVE') return 'ACTIVE';
                    const reqOverall = matchedReq?.overallStatus || matchedReq?.status;
                    if (reqOverall === 'APPROVED' || isApprovedFlag || currentStatus === 'APPROVED') return 'APPROVED';
                    if (reqOverall === 'REJECTED' || currentStatus === 'REJECTED') return 'REJECTED';
                    if (reqOverall === 'PENDING' || reqOverall === 'PENDING_APPROVAL' || reqOverall === 'PENDING_REVIEW' || currentStatus === 'PENDING_APPROVAL' || currentStatus === 'PENDING_REVIEW') {
                        return 'PENDING_REVIEW';
                    }
                    if (currentStatus === 'ARCHIVED') return 'ARCHIVED';
                    return 'DRAFT';
                };

                // A. Process session savedRules (from current wizard flow)
                if (savedRules && savedRules.length > 0) {
                    savedRules.forEach((sr, idx) => {
                        const key = (sr.ruleName || `rule-session-${idx}`).toLowerCase();
                        seenRuleKeys.add(key);
                        const matchReq = findMatchingApproval(sr.backendId, sr.ruleName);
                        const statusVal = deriveStatus(
                            (sr as any).Is_Active,
                            (sr as any).Is_Approved || sr.status === 'APPROVED',
                            sr.status,
                            matchReq
                        );

                        mergedRules.push({
                            id: sr.id || idx + 1,
                            backendId: sr.backendId || matchReq?.entityId,
                            ruleName: sr.ruleName,
                            signal: sr.ewsSignalName || matchReq?.entitySnapshot?.signal || 'EWS Signal',
                            domainComponentLayer: `${sr.domain || sr.metric || 'Volume'} > ${sr.dataSourceLabel || sr.component || 'Ingestion'} > ${sr.layer || 'All'}`,
                            domain: sr.domain || sr.metric || 'Volume',
                            component: sr.component || 'adf',
                            layer: sr.layer || 'bronze',
                            riskLevel: (sr.riskLevel || 'medium').toLowerCase(),
                            status: statusVal,
                            overallStatus: matchReq?.overallStatus || statusVal,
                            metricKPI: sr.metricKPI || 'Volume',
                            dataSource: sr.dataSourceLabel || sr.dataSourceValue || 'Ingestion Metrics',
                            detectionLogic: sr.detectionLogic || '',
                            handlerAgent: sr.handler || 'Pepwise DB',
                            notes: sr.notes || '',
                            createdAt: new Date().toLocaleString()
                        });
                    });
                }

                // B. Process fetched rule configs from backend DB
                if (fetchedConfigs.length > 0) {
                    fetchedConfigs.forEach((fc, idx) => {
                        const rName = fc.Rule_Name || fc.ruleName || fc.EWS_Signal_Name || `Rule ${idx + 1}`;
                        const key = rName.toLowerCase();
                        if (seenRuleKeys.has(key)) return;
                        seenRuleKeys.add(key);

                        const matchReq = findMatchingApproval(fc._id, rName);
                        const statusVal = deriveStatus(
                            fc.Is_Active,
                            fc.Is_Approved,
                            fc.status,
                            matchReq
                        );

                        const domain = fc.Domain || fc.domain || fc.Metric_KPI || 'Volume';
                        const component = fc.Component || fc.component || 'adf';
                        const layer = fc.Layer_Tool || fc.layer || 'bronze';
                        const dataSource = fc.Data_Source || fc.dataSource || 'Ingestion Metrics';

                        mergedRules.push({
                            id: mergedRules.length + 1,
                            backendId: fc._id || matchReq?.entityId,
                            ruleName: rName,
                            signal: fc.EWS_Signal_Name || fc.signal || matchReq?.entitySnapshot?.signal || 'EWS Signal',
                            domainComponentLayer: `${domain} > ${dataSource} > ${layer}`,
                            domain,
                            component,
                            layer,
                            riskLevel: (fc.Risk_Level || fc.riskLevel || 'medium').toLowerCase(),
                            status: statusVal,
                            overallStatus: matchReq?.overallStatus || statusVal,
                            metricKPI: fc.Metric_KPI || fc.metricKPI || 'Volume',
                            dataSource,
                            detectionLogic: fc.Detection_Logic_Trend_Pattern || fc.detectionLogic || '',
                            handlerAgent: fc.Handler || fc.handler || 'Pepwise DB',
                            notes: fc.Notes || fc.notes || '',
                            createdAt: fc.Created_Date ? new Date(fc.Created_Date).toLocaleString() : new Date().toLocaleString()
                        });
                    });
                }

                // Note: approval requests whose rule no longer exists in fetchedConfigs
                // (i.e. the rule was deleted after being submitted for approval) are
                // intentionally not resurrected as rule cards here - a request can only
                // ever be created for a rule that existed at the time, so one that no
                // longer matches a current RuleConfig means that rule was deleted.

                if (isMounted) {
                    setRules(mergedRules);
                    if (mergedRules.length > 0) {
                        setSelectedRuleId((prev) => (prev !== null ? prev : mergedRules[0].id));
                    }
                }
            } catch (err) {
                console.log('Activation live rules fetch note:', err);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchActivationData();
        return () => {
            isMounted = false;
        };
    }, [savedRules]);

    // Dynamic Filter Options built from metadata and active rules
    const domainOptions = useMemo(() => {
        const set = new Set<string>();
        metadata.forEach((m) => { if (m.Domain) set.add(m.Domain); });
        rules.forEach((r) => { if (r.domain) set.add(r.domain); });
        METRIC_DOMAIN_OPTIONS.forEach((o) => { if (o.value !== 'all') set.add(o.label); });
        return [
            { value: 'all', label: 'All' },
            ...Array.from(set).map((d) => ({ value: d, label: d }))
        ];
    }, [metadata, rules]);

    const componentOptions = useMemo(() => {
        const set = new Set<string>();
        metadata.forEach((m) => { if (m.Component) set.add(m.Component); });
        rules.forEach((r) => { if (r.component) set.add(r.component); });
        COMPONENT_FILTER_OPTIONS.forEach((o) => { if (o.value !== 'all') set.add(o.label); });
        return [
            { value: 'all', label: 'All' },
            ...Array.from(set).map((c) => ({ value: c, label: c }))
        ];
    }, [metadata, rules]);

    const layerOptions = useMemo(() => {
        const set = new Set<string>();
        metadata.forEach((m) => { if (m.Layer_Tool) set.add(m.Layer_Tool); });
        rules.forEach((r) => { if (r.layer) set.add(r.layer); });
        LAYER_TOOL_OPTIONS.forEach((o) => { if (o.value !== 'all') set.add(o.label); });
        return [
            { value: 'all', label: 'All' },
            ...Array.from(set).map((l) => ({ value: l, label: l }))
        ];
    }, [metadata, rules]);

    const activeCount = rules.filter((r) => r.status === 'ACTIVE').length;
    const approvedCount = rules.filter((r) => r.status === 'APPROVED' || r.status === 'ACTIVE').length;

    useEffect(() => {
        const hasActiveRule = rules.some((r) => r.status === 'ACTIVE');
        onActivationComplete?.(hasActiveRule);
    }, [rules, onActivationComplete]);

    const handleClearFilters = () => {
        setMetricDomainFilter('all');
        setComponentFilter('all');
        setLayerToolFilter('all');
        setStatusFilter('all');
    };

    // Filter rules based on all top-section filter criteria
    const filteredRules = useMemo(() => {
        return rules.filter((rule) => {
            if (metricDomainFilter !== 'all') {
                const matchDomain = rule.domain?.toLowerCase() === metricDomainFilter.toLowerCase() ||
                                    rule.domainComponentLayer?.toLowerCase().includes(metricDomainFilter.toLowerCase());
                if (!matchDomain) return false;
            }
            if (componentFilter !== 'all') {
                const matchComponent = rule.component?.toLowerCase() === componentFilter.toLowerCase() ||
                                       rule.domainComponentLayer?.toLowerCase().includes(componentFilter.toLowerCase()) ||
                                       rule.dataSource?.toLowerCase().includes(componentFilter.toLowerCase());
                if (!matchComponent) return false;
            }
            if (layerToolFilter !== 'all') {
                const matchLayer = rule.layer?.toLowerCase() === layerToolFilter.toLowerCase() ||
                                   rule.domainComponentLayer?.toLowerCase().includes(layerToolFilter.toLowerCase());
                if (!matchLayer) return false;
            }
            if (statusFilter !== 'all') {
                if (statusFilter === 'PENDING_REVIEW') {
                    if (rule.status !== 'PENDING_REVIEW' && (rule.status as any) !== 'PENDING_APPROVAL' && (rule.status as any) !== 'PENDING') {
                        return false;
                    }
                } else if (rule.status !== statusFilter) {
                    return false;
                }
            }
            return true;
        });
    }, [rules, metricDomainFilter, componentFilter, layerToolFilter, statusFilter]);

    const selectedRule = useMemo(() => {
        if (selectedRuleId != null) {
            const found = filteredRules.find((r) => r.id === selectedRuleId);
            if (found) return found;
        }
        return filteredRules[0] || rules.find((r) => r.id === selectedRuleId) || rules[0];
    }, [filteredRules, rules, selectedRuleId]);

    const handleToggleActivation = async (rule: ActivationRule) => {
        if (ENFORCE_APPROVAL_BEFORE_ACTIVATION && rule.status !== 'APPROVED' && rule.status !== 'ACTIVE') {
            setSnackbar({ open: true, message: 'This rule is not fully approved yet and cannot be activated.', severity: 'info' });
            return;
        }

        const isCurrentlyActive = rule.status === 'ACTIVE';
        const nextStatus: 'APPROVED' | 'ACTIVE' = isCurrentlyActive ? 'APPROVED' : 'ACTIVE';
        const nextAction = isCurrentlyActive ? 'deactivated' : 'activated';
        const nextIsActive = !isCurrentlyActive;

        if (rule.backendId) {
            try {
                await updateRuleConfig(rule.backendId, {
                    Is_Active: nextIsActive,
                    Is_Approved: true
                });
            } catch (err: any) {
                console.log('Error updating rule activation state in backend:', err);
                const msg = err?.response?.data?.message || err?.message || `Failed to ${nextAction} rule on server.`;
                setSnackbar({ open: true, message: msg, severity: 'error' });
                return;
            }
        }

        setRules((prev) => prev.map((r) => {
            if (r.id === rule.id) {
                return { ...r, status: nextStatus };
            }
            if (!isCurrentlyActive && nextStatus === 'ACTIVE' && r.ruleName.toLowerCase() === rule.ruleName.toLowerCase() && r.id !== rule.id && r.status === 'ACTIVE') {
                return { ...r, status: 'ARCHIVED' as const };
            }
            return r;
        }));

        const newLog: ActivationLogEvent = {
            id: Date.now(),
            timestamp: new Date().toLocaleString(),
            action: `Rule "${rule.ruleName}" was ${nextAction}. Status is now ${nextStatus}.`
        };
        setLogs((prev) => [newLog, ...prev]);

        setSnackbar({
            open: true,
            message: `"${rule.ruleName}" ${nextAction} successfully.`,
            severity: 'success'
        });
    };

    return (
        <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <InfoIcon sx={{ color: '#a0aab4', fontSize: '1.2rem', mr: 1 }} />
                <Typography sx={{ fontStyle: 'italic', color: '#a0aab4', fontSize: '0.9rem' }}>
                    This section is to manage rule activation status and deployment states.
                </Typography>
            </Box>

            {/* Top Filter Bar */}
            <Box sx={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                p: 2.5,
                border: '1px solid #e2e8f0',
                mb: 3,
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 200, flex: 1 }}>
                        <StyledSelect
                            label="Metric Domain"
                            value={metricDomainFilter}
                            onChange={(val) => setMetricDomainFilter(val)}
                            options={domainOptions}
                        />
                    </Box>
                    <Box sx={{ minWidth: 200, flex: 1 }}>
                        <StyledSelect
                            label="Component"
                            value={componentFilter}
                            onChange={(val) => setComponentFilter(val)}
                            options={componentOptions}
                        />
                    </Box>
                    <Box sx={{ minWidth: 200, flex: 1 }}>
                        <StyledSelect
                            label="Layer / Tool"
                            value={layerToolFilter}
                            onChange={(val) => setLayerToolFilter(val)}
                            options={layerOptions}
                        />
                    </Box>
                    <Box sx={{ minWidth: 200, flex: 1 }}>
                        <StyledSelect
                            label="Status"
                            value={statusFilter}
                            onChange={(val) => setStatusFilter(val)}
                            options={STATUS_FILTER_OPTIONS}
                        />
                    </Box>
                    <Button
                        variant="text"
                        startIcon={<CloseIcon />}
                        onClick={handleClearFilters}
                        sx={{ color: '#39ACE7', fontWeight: 600, textTransform: 'uppercase', mt: 2 }}
                    >
                        CLEAR ALL
                    </Button>
                </Box>
            </Box>

            {/* Main Content: Left Rules List & Right Detail Panel */}
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                {/* Left Column: Rules List */}
                <Box sx={{ flex: '1 1 65%', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ fontWeight: 600, color: '#003771', fontSize: '1.05rem' }}>
                            Configured EWS Rules ({filteredRules.length})
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, fontSize: '0.85rem' }}>
                            <Typography sx={{ color: '#2e7d32', fontWeight: 600 }}>Active: {activeCount}</Typography>
                            <Typography sx={{ color: '#64748b' }}>Approved: {approvedCount}</Typography>
                        </Box>
                    </Box>

                    {isLoading ? (
                        <Box sx={{ p: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#fff', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                            <LoaderComponent size={40} />
                        </Box>
                    ) : filteredRules.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center', backgroundColor: '#fff', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                            <Typography sx={{ color: '#a0aab4', fontStyle: 'italic' }}>
                                No configured rules match the selected filter criteria.
                            </Typography>
                        </Box>
                    ) : (
                        filteredRules.map((rule) => {
                            const isSelected = selectedRule?.id === rule.id;
                            const isActive = rule.status === 'ACTIVE';
                            const isApproved = rule.status === 'APPROVED';
                            const isPending = rule.status === 'PENDING_REVIEW' || rule.status === 'DRAFT';

                            return (
                                <Box
                                    key={rule.id}
                                    onClick={() => setSelectedRuleId(rule.id)}
                                    sx={{
                                        backgroundColor: '#fff',
                                        borderRadius: 1,
                                        p: 2,
                                        mb: 2,
                                        border: isSelected ? '2px solid #39ACE7' : '1px solid #e2e8f0',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        '&:hover': { borderColor: '#39ACE7' }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#333' }}>
                                                {rule.ruleName}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>
                                                {rule.domainComponentLayer} | {rule.metricKPI}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box sx={{
                                                backgroundColor: getRiskColor(rule.riskLevel),
                                                color: '#fff',
                                                px: 1.5,
                                                py: 0.2,
                                                borderRadius: '12px',
                                                fontSize: '0.65rem',
                                                fontWeight: 700,
                                                textTransform: 'uppercase'
                                            }}>
                                                {rule.riskLevel}
                                            </Box>
                                            <Tooltip title={isPending ? 'Rule must be approved before activation' : ''}>
                                                <span>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleToggleActivation(rule);
                                                        }}
                                                        disabled={isPending}
                                                        sx={{
                                                            backgroundColor: isActive ? '#e53935' : '#2e7d32',
                                                            color: '#fff',
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            '&:hover': { backgroundColor: isActive ? '#c62828' : '#1b5e20' }
                                                        }}
                                                    >
                                                        {isActive ? 'DEACTIVATE' : 'ACTIVATE'}
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })
                    )}
                </Box>

                {/* Right Column: Selected Rule Identity & Metadata */}
                <Box sx={{
                    flex: '1 1 35%',
                    backgroundColor: '#fff',
                    borderRadius: 1,
                    p: 2.5,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    border: '1px solid #e2e8f0'
                }}>
                    <SectionHeader title="Rule Identity Summary" />
                    {selectedRule ? (
                        <Box sx={{ mt: 2 }}>
                            {([
                                ['Rule Name', selectedRule.ruleName],
                                ['Signal', selectedRule.signal],
                                ['Domain > Component > Layer', selectedRule.domainComponentLayer],
                                ['Status', selectedRule.status],
                                ['Metric / KPI', selectedRule.metricKPI],
                                ['Data Source', selectedRule.dataSource],
                                ['Detection Logic', selectedRule.detectionLogic || '—'],
                                ['Handler / Agent', selectedRule.handlerAgent],
                                ['Notes', selectedRule.notes || '—'],
                                ['Created At', selectedRule.createdAt]
                            ] as [string, string][]).map(([label, value]) => (
                                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, gap: 2 }}>
                                    <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>{label}</Typography>
                                    <Typography sx={{ fontSize: '0.8rem', color: '#333', fontWeight: 600, textAlign: 'right' }}>{value}</Typography>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: '0.85rem', color: '#a0aab4', fontStyle: 'italic', py: 3, textAlign: 'center' }}>
                            Select a rule from the left panel to inspect identity details.
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Activation Logs */}
            {logs.length > 0 && (
                <Box sx={{ backgroundColor: '#fff', borderRadius: 1, p: 2, border: '1px solid #e2e8f0', mt: 3, mb: 3 }}>
                    <SectionHeader title="Activation Audit Log" />
                    <Box sx={{ mt: 1.5 }}>
                        {logs.map((log) => (
                            <Box key={log.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f1f5f9', fontSize: '0.8rem' }}>
                                <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>{log.timestamp}</Typography>
                                <Typography sx={{ fontSize: '0.8rem', color: '#003771', fontWeight: 500 }}>{log.action}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ActivationTab;
